import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Copy,
  Users,
  Coins,
  Plus,
  Send,
  Sparkles,
  Loader2,
  Network,
  Plane,
  MapPin,
  Star,
  Crown,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const TRIP_IMAGES = {
  goa: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?auto=format&fit=crop&w=1200&q=80",
  kerala: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?auto=format&fit=crop&w=1200&q=80",
  thailand: "https://images.unsplash.com/photo-1528181304800-259b08848526?auto=format&fit=crop&w=1200&q=80",
};

function StatCard({ label, value, sub, testid, accent }) {
  return (
    <div data-testid={testid} className="card-elev rounded-md p-6 md:p-7">
      <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`font-display text-3xl md:text-4xl mt-3 ${accent ? "text-gold" : "text-white"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-2 font-mono-x">{sub}</div>}
    </div>
  );
}

function RankCard({ rank }) {
  if (!rank) return null;
  const current = rank.current;
  const next = rank.next;
  const ranks = rank.ranks || [];
  return (
    <div data-testid="rank-card" className="card-elev rounded-md p-6 md:p-8 mb-10 relative overflow-hidden">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
              <Crown className="w-3.5 h-3.5" /> Team rank
            </div>
            <div className="font-display text-3xl">
              {current ? (
                <>You've reached <span className="text-gold italic">{current.name}</span></>
              ) : (
                <>Build your team to earn <span className="text-gold italic">VF 1 STAR</span></>
              )}
            </div>
            <div className="text-slate-400 mt-2 text-sm">
              Team investment (cumulative across all 25 downline levels):{" "}
              <span className="font-mono-x text-gold">${(rank.team_investment ?? 0).toLocaleString()}</span>
            </div>
          </div>
          {current && (
            <div data-testid="rank-badge" className="flex items-center gap-2 px-4 py-2 rounded-full bg-gold/15 border border-gold/40">
              <div className="flex">
                {Array.from({ length: current.star }).map((_, i) => (
                  <Star key={i} className="w-3.5 h-3.5 fill-current text-gold -mr-1" />
                ))}
              </div>
              <span className="text-gold font-display text-sm ml-2">{current.name}</span>
            </div>
          )}
        </div>

        {next && (
          <div className="mt-6">
            <div className="flex justify-between text-xs font-mono-x text-slate-500 mb-2">
              <span>Progress to {next.name}</span>
              <span>${(rank.remaining_to_next ?? 0).toLocaleString()} to go</span>
            </div>
            <div className="h-2 bg-[#0b0c10] rounded-full overflow-hidden border border-[#272a30]">
              <div className="h-full bg-gradient-to-r from-gold to-amber-300 transition-all" style={{ width: `${rank.progress_pct}%` }} />
            </div>
          </div>
        )}

        <div className="mt-8 grid grid-cols-3 md:grid-cols-9 gap-2">
          {ranks.map((r) => {
            const achieved = current && current.star >= r.star;
            const isNext = next && next.star === r.star;
            return (
              <div
                key={r.star}
                data-testid={`rank-tier-${r.star}`}
                className={`text-center rounded-md py-3 px-2 border transition-all ${
                  achieved ? "border-gold bg-gold/10" : isNext ? "border-gold/40" : "border-[#272a30]"
                }`}
              >
                <div className="flex justify-center gap-0.5 mb-1">
                  {Array.from({ length: r.star }).slice(0, 5).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-2.5 h-2.5 ${achieved ? "fill-current text-gold" : "text-slate-600"}`}
                    />
                  ))}
                  {r.star > 5 && <span className={`text-xs font-mono-x ml-1 ${achieved ? "text-gold" : "text-slate-600"}`}>+{r.star - 5}</span>}
                </div>
                <div className={`text-[10px] uppercase tracking-widest ${achieved ? "text-gold" : "text-slate-500"}`}>{r.name.replace("VF ", "").replace(" STAR", "★")}</div>
                <div className={`text-xs font-mono-x mt-1 ${achieved ? "text-white" : "text-slate-500"}`}>
                  ${r.threshold >= 1000000 ? (r.threshold / 1000000).toFixed(2) + "M" : (r.threshold / 1000) + "K"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function TripStatusCard({ trip, totalInvested }) {
  if (!trip) return null;
  const unlocked = trip.unlocked;
  const next = trip.next;
  const tiers = trip.tiers || [];
  const progress = next
    ? Math.min(100, (totalInvested / next.threshold) * 100)
    : 100;

  return (
    <div data-testid="trip-status-card" className="card-elev rounded-md p-6 md:p-8 mb-10 relative overflow-hidden">
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="relative">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
              <Plane className="w-3.5 h-3.5" /> Launch offer · Travel rewards
            </div>
            <div className="font-display text-3xl">
              {unlocked ? (
                <>You unlocked <span className="text-gold italic">{unlocked.name}</span></>
              ) : (
                <>Invest to unlock your first <span className="text-gold italic">getaway</span></>
              )}
            </div>
            {next ? (
              <div className="text-slate-400 mt-2 text-sm">
                <span className="text-gold font-mono-x">${trip.remaining_to_next.toLocaleString()}</span> away from <span className="text-white">{next.name}</span> ({next.destination})
              </div>
            ) : (
              <div className="text-slate-400 mt-2 text-sm">Congratulations — you've unlocked the top-tier trip.</div>
            )}
          </div>
          {unlocked && (
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 rounded-full">
              <MapPin className="w-3 h-3" /> {unlocked.destination}
            </div>
          )}
        </div>

        {/* progress bar */}
        {next && (
          <div className="mt-6">
            <div className="h-2 bg-[#0b0c10] rounded-full overflow-hidden border border-[#272a30]">
              <div className="h-full bg-gradient-to-r from-gold to-amber-300 transition-all" style={{ width: `${progress}%` }} />
            </div>
            <div className="flex justify-between text-xs font-mono-x text-slate-500 mt-2">
              <span>${totalInvested.toLocaleString()} invested</span>
              <span>Target ${next.threshold.toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* tiers */}
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          {tiers.map((t, idx) => {
            const isUnlocked = totalInvested >= t.threshold;
            return (
              <div
                key={t.id}
                data-testid={`trip-tier-card-${t.id}`}
                className={`rounded-md overflow-hidden border transition-colors ${isUnlocked ? "border-gold" : "border-[#272a30]"}`}
              >
                <div className="aspect-[16/8] overflow-hidden relative">
                  <img src={TRIP_IMAGES[t.id]} alt={t.destination} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute top-3 left-3 text-xs uppercase tracking-widest px-2 py-1 rounded-full bg-black/50 backdrop-blur border border-white/10">
                    Tier {idx + 1}
                  </div>
                  {isUnlocked && (
                    <div className="absolute top-3 right-3 text-xs uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/20 backdrop-blur border border-emerald-500/40 text-emerald-300">
                      Unlocked
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 right-3">
                    <div className="font-display text-xl text-white">{t.name}</div>
                    <div className="text-slate-300 text-xs">{t.destination}</div>
                  </div>
                </div>
                <div className="px-4 py-3 flex justify-between items-center bg-[#0b0c10]">
                  <span className="text-xs uppercase text-slate-500 tracking-widest">Threshold</span>
                  <span className={`font-mono-x ${isUnlocked ? "text-gold" : "text-slate-300"}`}>${t.threshold.toLocaleString()}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InvestmentRow({ inv }) {
  const state = inv;
  const badge = {
    pending: "bg-amber-500/10 text-amber-300 border-amber-400/30",
    active: "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
    completed: "bg-cyan-500/10 text-cyan-300 border-cyan-400/30",
    rejected: "bg-red-500/10 text-red-300 border-red-400/30",
  }[state.status] || "bg-slate-500/10 text-slate-300 border-slate-400/30";

  return (
    <div data-testid={`inv-row-${state.id}`} className="border-b border-[#272a30] py-5 grid md:grid-cols-6 gap-4 items-center">
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">{state.package_name}</div>
        <div className="font-display text-xl mt-1">${state.amount.toLocaleString()}</div>
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">Multiplier</div>
        <div className="font-mono-x text-white mt-1">{state.multiplier}×</div>
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">Earned</div>
        <div className="font-mono-x text-gold mt-1">${state.earned.toLocaleString()}</div>
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">Days</div>
        <div className="font-mono-x text-white mt-1">{Math.floor(state.days_elapsed)} / {state.duration_days}</div>
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-widest">Progress</div>
        <div className="h-1.5 bg-[#0b0c10] rounded-full mt-2 overflow-hidden border border-[#272a30]">
          <div className="h-full bg-gold" style={{ width: `${Math.min(100, state.progress_pct)}%` }} />
        </div>
      </div>
      <div>
        <span className={`text-xs px-3 py-1 rounded-full border uppercase tracking-widest ${badge}`}>{state.status}</span>
      </div>
    </div>
  );
}

/** Build a 30-day daily ROI projection from all active investments */
function buildRoiSeries(investments) {
  const active = investments.filter((i) => i.status === "active" || i.status === "completed");
  if (active.length === 0) return [];
  const days = 30;
  const now = new Date();
  const series = [];
  for (let i = -days + 1; i <= 0; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    let cum = 0;
    for (const inv of active) {
      if (!inv.start_date) continue;
      const start = new Date(inv.start_date);
      const daysSinceStart = Math.max(0, (d - start) / (1000 * 60 * 60 * 24));
      const capped = Math.min(daysSinceStart, inv.duration_days);
      const earned = Math.min(inv.amount * inv.daily_roi * capped, inv.amount * (inv.multiplier - 1));
      cum += earned;
    }
    series.push({
      date: d.toISOString().slice(5, 10),
      earned: Math.round(cum * 100) / 100,
    });
  }
  return series;
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [inv, setInv] = useState([]);
  const [wds, setWds] = useState([]);
  const [refs, setRefs] = useState({ referrals: [], commissions: [], referral_code: user?.referral_code, level_income: { per_level: [], total: 0 }, downline_by_level: {} });

  const [wAmount, setWAmount] = useState("");
  const [wCrypto, setWCrypto] = useState("USDT_TRC20");
  const [wAddr, setWAddr] = useState("");
  const [wBusy, setWBusy] = useState(false);

  const loadAll = async () => {
    const [d, i, w, r] = await Promise.all([
      api.get("/dashboard"),
      api.get("/investments/mine"),
      api.get("/withdrawals/mine"),
      api.get("/referrals/mine"),
    ]);
    setDash(d.data);
    setInv(i.data);
    setWds(w.data);
    setRefs(r.data);
  };

  useEffect(() => {
    loadAll().catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line
  }, []);

  const roiSeries = useMemo(() => buildRoiSeries(inv), [inv]);

  const refLink = `${window.location.origin}/register?ref=${refs.referral_code || user?.referral_code || ""}`;

  const copyRefLink = () => {
    navigator.clipboard.writeText(refLink);
    toast.success("Referral link copied");
  };

  const submitWithdraw = async (e) => {
    e.preventDefault();
    setWBusy(true);
    try {
      await api.post("/withdrawals", {
        amount: parseFloat(wAmount),
        crypto: wCrypto,
        to_wallet: wAddr.trim(),
      });
      toast.success("Withdrawal request submitted");
      setWAmount(""); setWAddr("");
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setWBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07080a] text-slate-100 noise">
      <Navbar />

      <div className="pt-24 max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Member Dashboard</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight">
              Welcome back, <span className="text-gold italic">{user?.name?.split(" ")[0]}</span>
            </h1>
          </div>
          <Button
            data-testid="new-investment-btn"
            onClick={() => nav("/invest")}
            className="bg-gold text-black hover:bg-[#f3e396] rounded-full px-6 h-11"
          >
            <Plus className="w-4 h-4 mr-2" /> New investment
          </Button>
        </div>

        {/* Stats row */}
        <div className="grid md:grid-cols-12 gap-5 mb-10">
          <div className="md:col-span-3"><StatCard testid="stat-available" label="Available balance" value={`$${(dash?.available_balance ?? 0).toLocaleString()}`} sub={`Withdrawn $${(dash?.withdrawn ?? 0).toLocaleString()}`} accent /></div>
          <div className="md:col-span-3"><StatCard testid="stat-invested" label="Total invested" value={`$${(dash?.total_invested ?? 0).toLocaleString()}`} /></div>
          <div className="md:col-span-3"><StatCard testid="stat-earned" label="ROI earned" value={`$${(dash?.total_earned ?? 0).toLocaleString()}`} /></div>
          <div className="md:col-span-3"><StatCard testid="stat-referral-total" label="Referral earnings" value={`$${(dash?.referral_earnings ?? 0).toLocaleString()}`} sub={`Direct $${(dash?.direct_referral_earnings ?? 0).toLocaleString()} · Level $${(dash?.level_income ?? 0).toLocaleString()}`} /></div>
        </div>

        {/* Rank card */}
        <RankCard rank={dash?.rank_status} />

        {/* Trip reward card */}
        <TripStatusCard trip={dash?.trip_status} totalInvested={dash?.total_invested ?? 0} />

        {/* Chart + Referral network summary */}
        <div className="grid md:grid-cols-12 gap-6 mb-10">
          <div data-testid="roi-chart-card" className="md:col-span-8 card-elev rounded-md p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400">ROI performance</div>
                <div className="font-display text-2xl mt-1">Cumulative earned <span className="text-gold">·</span> last 30 days</div>
              </div>
              <div className="text-xs font-mono-x text-slate-500">{inv.filter((i)=>i.status==="active").length} active</div>
            </div>
            {roiSeries.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-slate-500 font-mono-x text-sm">No active investments yet — chart appears after your first approved investment.</div>
            ) : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <AreaChart data={roiSeries} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="gd" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#272a30" strokeDasharray="2 4" vertical={false} />
                    <XAxis dataKey="date" stroke="#64748b" tick={{ fill: "#64748b", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
                    <YAxis stroke="#64748b" tick={{ fill: "#64748b", fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} tickFormatter={(v)=>`$${v}`} />
                    <Tooltip
                      contentStyle={{ background: "#111317", border: "1px solid #272a30", borderRadius: 6, fontSize: 12 }}
                      labelStyle={{ color: "#94a3b8" }}
                      itemStyle={{ color: "#D4AF37" }}
                      formatter={(v)=>[`$${v}`, "Earned"]}
                    />
                    <Area type="monotone" dataKey="earned" stroke="#D4AF37" strokeWidth={2} fill="url(#gd)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div data-testid="network-summary-card" className="md:col-span-4 card-elev rounded-md p-6 md:p-8 flex flex-col">
            <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-2"><Network className="w-3.5 h-3.5 text-gold" /> Referral network</div>
            <div className="font-display text-4xl mt-3">{dash?.downline_total ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1 font-mono-x">Downline members across 25 levels</div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <div className="card-elev rounded-md p-4">
                <div className="text-xs uppercase text-slate-500">Direct (L1)</div>
                <div className="font-display text-2xl text-gold mt-1">{dash?.referrals_count ?? 0}</div>
              </div>
              <div className="card-elev rounded-md p-4">
                <div className="text-xs uppercase text-slate-500">Level income</div>
                <div className="font-display text-2xl text-gold mt-1">${(dash?.level_income ?? 0).toLocaleString()}</div>
              </div>
            </div>

            <div className="divider-gold my-6" />
            <div className="text-xs text-slate-500 mb-2">Your referral code</div>
            <div className="font-mono-x text-gold text-lg">{refs.referral_code || user?.referral_code}</div>
          </div>
        </div>

        {/* Referral CTA card */}
        <div data-testid="referral-card" className="card-elev rounded-md p-8 mb-10 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Referral program</div>
              <div className="font-display text-3xl">Unlock the <span className="text-gold italic">3× tier</span> + 25 levels of income</div>
              <p className="text-slate-400 mt-3 max-w-md">
                Share your unique link. Get <span className="text-gold font-semibold">5% direct commission</span> on every referred investment, plus level ROI income up to 25 levels deep — <span className="text-gold">12% at Level 1</span>, tapering across the tree.
              </p>
            </div>
            <div>
              <Label className="text-slate-300">Your referral link</Label>
              <div className="mt-2 flex items-stretch gap-2">
                <div className="flex-1 card-elev rounded-md px-4 py-3 font-mono-x text-sm text-slate-200 overflow-hidden text-ellipsis whitespace-nowrap">
                  {refLink}
                </div>
                <Button data-testid="copy-ref-link" onClick={copyRefLink} className="bg-gold text-black hover:bg-[#f3e396]">
                  <Copy className="w-4 h-4 mr-2" /> Copy
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="investments" className="mb-16">
          <TabsList data-testid="dash-tabs" className="bg-[#111317] border border-[#272a30]">
            <TabsTrigger data-testid="tab-investments" value="investments">Investments</TabsTrigger>
            <TabsTrigger data-testid="tab-withdraw" value="withdraw">Withdraw</TabsTrigger>
            <TabsTrigger data-testid="tab-referrals" value="referrals">Referrals</TabsTrigger>
            <TabsTrigger data-testid="tab-levels" value="levels">Level Income</TabsTrigger>
          </TabsList>

          <TabsContent value="investments">
            <div className="card-elev rounded-md p-6 md:p-8 mt-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-display text-2xl">Your investments</div>
                  <div className="text-slate-400 text-sm mt-1">All submissions & active ROI accruals</div>
                </div>
                <Button data-testid="inv-refresh" variant="outline" onClick={loadAll} className="bg-transparent border-white/10 text-white hover:bg-white/5">Refresh</Button>
              </div>
              {inv.length === 0 && (
                <div data-testid="no-investments" className="text-slate-500 py-12 text-center font-mono-x text-sm">
                  No investments yet. Start with a Premium or Secure package.
                </div>
              )}
              {inv.map((i) => <InvestmentRow key={i.id} inv={i} />)}
            </div>
          </TabsContent>

          <TabsContent value="withdraw">
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <form onSubmit={submitWithdraw} className="card-elev rounded-md p-8">
                <div className="font-display text-2xl mb-6 flex items-center gap-3"><Send className="w-5 h-5 text-gold" /> Request withdrawal</div>
                <div className="space-y-5">
                  <div>
                    <Label className="text-slate-300">Amount (USD)</Label>
                    <Input
                      data-testid="wd-amount"
                      type="number"
                      min={dash?.min_withdrawal_usd ?? 10}
                      step="0.01"
                      required
                      value={wAmount}
                      onChange={(e) => setWAmount(e.target.value)}
                      className="mt-2 bg-[#0b0c10] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11"
                    />
                    <div className="text-xs text-slate-500 mt-2">
                      Available: <span className="font-mono-x text-gold">${(dash?.available_balance ?? 0).toLocaleString()}</span>
                      <span className="mx-2">·</span>
                      Min withdrawal: <span className="font-mono-x text-gold">${dash?.min_withdrawal_usd ?? 10}</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-300">Crypto</Label>
                    <select
                      data-testid="wd-crypto"
                      value={wCrypto}
                      onChange={(e) => setWCrypto(e.target.value)}
                      className="mt-2 w-full bg-[#0b0c10] border border-[#272a30] rounded-md h-11 px-3 text-white focus:border-gold outline-none"
                    >
                      <option value="USDT_TRC20">USDT (TRC20)</option>
                      <option value="BTC">Bitcoin</option>
                      <option value="ETH">Ethereum</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Destination wallet address</Label>
                    <Input
                      data-testid="wd-address"
                      required
                      value={wAddr}
                      onChange={(e) => setWAddr(e.target.value)}
                      className="mt-2 bg-[#0b0c10] border-[#272a30] focus:border-gold focus:ring-gold text-white h-11 font-mono-x"
                      placeholder="Your wallet address"
                    />
                  </div>
                  <Button
                    data-testid="wd-submit"
                    disabled={wBusy}
                    className="bg-gold text-black hover:bg-[#f3e396] h-11 rounded-full w-full"
                  >
                    {wBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit request"}
                  </Button>
                </div>
              </form>

              <div className="card-elev rounded-md p-8">
                <div className="font-display text-2xl mb-6">Withdrawal history</div>
                {wds.length === 0 && <div data-testid="no-withdrawals" className="text-slate-500 py-8 text-center font-mono-x text-sm">No withdrawal requests yet.</div>}
                {wds.map((w) => (
                  <div key={w.id} data-testid={`wd-row-${w.id}`} className="border-b border-[#272a30] py-4 flex justify-between items-center gap-4">
                    <div>
                      <div className="font-display text-lg">${w.amount.toLocaleString()} <span className="text-slate-500 text-sm font-sans">· {w.crypto}</span></div>
                      <div className="text-xs text-slate-500 font-mono-x mt-1 truncate max-w-xs">{w.to_wallet}</div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full border uppercase tracking-widest ${
                      w.status === "approved" ? "bg-emerald-500/10 text-emerald-300 border-emerald-400/30" :
                      w.status === "pending" ? "bg-amber-500/10 text-amber-300 border-amber-400/30" :
                      "bg-red-500/10 text-red-300 border-red-400/30"
                    }`}>{w.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="referrals">
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="card-elev rounded-md p-8">
                <div className="font-display text-2xl mb-2 flex items-center gap-3"><Users className="w-5 h-5 text-gold" /> Direct referrals (Level 1)</div>
                <div className="text-xs text-slate-500 mb-4 font-mono-x">You earn {refs.direct_commission_rate_pct ?? 5}% commission on each direct member's principal + 12% of their ROI.</div>
                {refs.referrals.length === 0 && <div data-testid="no-referrals" className="text-slate-500 py-8 text-center font-mono-x text-sm">Share your link to invite members.</div>}
                {refs.referrals.map((r) => (
                  <div key={r.id} data-testid={`ref-row-${r.id}`} className="border-b border-[#272a30] py-4 flex justify-between items-center">
                    <div>
                      <div className="text-white">{r.name}</div>
                      <div className="text-xs text-slate-500 font-mono-x">{r.email_masked}</div>
                    </div>
                    <div className="font-mono-x text-gold">${r.total_invested.toLocaleString()}</div>
                  </div>
                ))}
              </div>
              <div className="card-elev rounded-md p-8">
                <div className="font-display text-2xl mb-6 flex items-center gap-3"><Coins className="w-5 h-5 text-gold" /> Direct commissions</div>
                {refs.commissions.length === 0 && <div className="text-slate-500 py-8 text-center font-mono-x text-sm">No direct commissions yet.</div>}
                {refs.commissions.map((c) => (
                  <div key={c.id} className="border-b border-[#272a30] py-4 flex justify-between items-center">
                    <div className="text-slate-400 text-sm font-mono-x">{new Date(c.created_at).toLocaleDateString()}</div>
                    <div className="font-mono-x text-gold">+${c.amount.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="levels">
            <div className="card-elev rounded-md p-6 md:p-8 mt-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="font-display text-2xl">Level income breakdown</div>
                  <div className="text-slate-400 text-sm mt-1">
                    You earn a % of every downline's ROI, up to 25 levels deep. Total earned:{" "}
                    <span className="text-gold font-mono-x">${(refs?.level_income?.total ?? 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 uppercase tracking-widest text-xs border-b border-[#272a30]">
                      <th className="text-left py-3 pr-4">Level</th>
                      <th className="text-left py-3 pr-4">Rate</th>
                      <th className="text-left py-3 pr-4">Members</th>
                      <th className="text-left py-3 pr-4">Downline ROI</th>
                      <th className="text-right py-3">Your income</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(refs?.level_income?.per_level || []).map((l) => (
                      <tr key={l.level} data-testid={`level-row-${l.level}`} className="border-b border-[#272a30] hover:bg-white/5 transition-colors">
                        <td className="py-3 pr-4 font-mono-x text-white">L{l.level}</td>
                        <td className="py-3 pr-4 font-mono-x text-gold">{l.rate_pct}%</td>
                        <td className="py-3 pr-4 font-mono-x text-slate-300">{l.members_count}</td>
                        <td className="py-3 pr-4 font-mono-x text-slate-400">${l.downline_roi.toLocaleString()}</td>
                        <td className="py-3 text-right font-mono-x text-gold">${l.income.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
